import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Network, RefreshCw, Plug, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  rmmService,
  NinjaRmmConfig,
  RmmOrgMappingView,
  RmmMappingInput,
  RmmSyncResult,
  RmmReconciliationResult,
  RmmReconciliationCustomer,
} from '../../services/rmmService';
import { devicePullSummary, formatApiInstant, latestApiInstant, serverReportsSyncInterval } from '../../services/rmmSyncSchedule';
import { NinjaDeviceSyncStatus } from '../../components/integrations/NinjaDeviceSyncStatus';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { deviceProblemBadges, hasDeviceStatus, RmmDeviceStatusPanel, RmmFleetStatus } from '../../components/customers/RmmDeviceStatusPanel';

const REGIONS = [
  { value: 'oc', label: 'Oceania (AU/NZ)' },
  { value: 'us', label: 'United States' },
  { value: 'eu', label: 'Europe' },
  { value: 'ca', label: 'Canada' },
];

const errMessage = (e: unknown) =>
  (e as any)?.message || (e as any)?.error || 'Something went wrong';

const fmtDate = (iso?: string | null) => formatApiInstant(iso) || '—';

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const map: Record<string, { variant: any; label: string }> = {
    'auto-high': { variant: 'success', label: 'Strong match' },
    'auto-low': { variant: 'warning', label: 'Likely match' },
    manual: { variant: 'info', label: 'Manual' },
    unmapped: { variant: 'secondary', label: 'No match' },
  };
  const m = map[confidence] || map.unmapped;
  return <Badge variant={m.variant}>{m.label}</Badge>;
}

function CustomerRecon({ c }: { c: RmmReconciliationCustomer }) {
  const [open, setOpen] = useState(false);
  const problems =
    (c.ninjaStaleCount ?? 0) +
    (c.notInstalledCount ?? 0) +
    (c.installedNotSignedInCount ?? 0) +
    (c.agentQuietCount ?? 0) +
    (c.installUnknownCount ?? 0);
  const current = hasDeviceStatus(c) && !c.error && problems === 0;
  return (
    <div className="rounded-lg border border-slate-200">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left rounded-lg hover:bg-slate-50 transition-colors"
      >
        <div>
          <div className="font-medium text-slate-800">{c.customerName}</div>
          <div className="text-xs text-slate-500">{c.externalOrgName}</div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
          <span className="text-slate-500">{c.ninjaCount} Ninja</span>
          {deviceProblemBadges(c).map((badge) => (
            <Badge key={badge.label} variant={badge.variant}>{badge.label}</Badge>
          ))}
          {c.error && <Badge variant="secondary">error</Badge>}
          {current && <Badge variant="success">current</Badge>}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4">
          {c.error ? <div className="text-sm text-rose-600">{c.error}</div> : <RmmDeviceStatusPanel c={c} />}
        </div>
      )}
    </div>
  );
}

export default function NinjaRmmView() {
  const { partner, partnerAuthContext } = useAuth();
  const partnerId = partner?.partnerId || partnerAuthContext?.partnerInfo?.partnerId || '';
  const customers = partnerAuthContext?.managedCustomers ?? [];

  const [tab, setTab] = useState<'connection' | 'organizations' | 'reconciliation'>('connection');
  const [config, setConfig] = useState<NinjaRmmConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // connection form
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ region: 'oc', clientId: '', clientSecret: '', displayName: '' });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; text: string } | null>(null);

  // organizations
  const [orgs, setOrgs] = useState<RmmOrgMappingView[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [edits, setEdits] = useState<Record<string, { customerId: string; status: 'Confirmed' | 'Ignored' }>>({});
  const [syncing, setSyncing] = useState(false);
  const [savingMaps, setSavingMaps] = useState(false);
  const [savingInterval, setSavingInterval] = useState(false);
  const [syncResult, setSyncResult] = useState<RmmSyncResult | null>(null);

  // reconciliation
  const [recon, setRecon] = useState<RmmReconciliationResult | null>(null);
  const [reconLoading, setReconLoading] = useState(false);

  const loadConfig = useCallback(async (silent = false) => {
    if (!partnerId) return;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const c = await rmmService.getConfig(partnerId);
      setConfig(c);
      if (!silent) {
        setEditing(!c.connected);
        if (c.region) setForm((f) => ({ ...f, region: c.region! }));
      }
    } catch (e) {
      setError(errMessage(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // if the form has full creds, test those; otherwise test the stored config
      const hasFormCreds = !!form.clientId && !!form.clientSecret && !!form.region;
      const r = hasFormCreds
        ? await rmmService.testConnection(partnerId, {
            region: form.region,
            clientId: form.clientId.trim(),
            clientSecret: form.clientSecret.trim(),
          })
        : await rmmService.testConnection(partnerId);
      setTestResult({
        success: r.success,
        text: r.success ? `Connected (${r.latencyMs} ms)` : r.error || r.message || 'Connection failed',
      });
      if (!hasFormCreds) await loadConfig();
    } catch (e) {
      setTestResult({ success: false, text: errMessage(e) });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!form.region || !form.clientId || (!config?.hasSecret && !form.clientSecret)) {
      setError('Region, Client ID and Client Secret are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await rmmService.saveConfig(partnerId, {
        region: form.region,
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim() || undefined,
        displayName: form.displayName.trim() || undefined,
      });
      setConfig(saved);
      setEditing(false);
      setForm((f) => ({ ...f, clientSecret: '' }));
      setTestResult(null);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect NinjaRMM? Stored credentials will be removed.')) return;
    try {
      await rmmService.disconnect(partnerId);
      await loadConfig();
      setOrgs([]);
    } catch (e) {
      setError(errMessage(e));
    }
  };

  const loadOrgs = useCallback(async () => {
    if (!partnerId) return;
    setOrgsLoading(true);
    try {
      const list = await rmmService.getOrganizations(partnerId);
      setOrgs(list);
      const next: Record<string, { customerId: string; status: 'Confirmed' | 'Ignored' }> = {};
      for (const o of list) {
        const cust =
          o.status === 'Confirmed' ? o.mappedCustomerId : o.status === 'Ignored' ? '' : o.suggestedCustomerId;
        next[o.externalOrgId] = {
          customerId: cust || '',
          status: o.status === 'Ignored' ? 'Ignored' : 'Confirmed',
        };
      }
      setEdits(next);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setOrgsLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    if (tab === 'organizations' && config?.connected && orgs.length === 0) loadOrgs();
  }, [tab, config?.connected, orgs.length, loadOrgs]);

  const loadRecon = useCallback(async () => {
    if (!partnerId) return;
    setReconLoading(true);
    setError(null);
    try {
      const r = await rmmService.getReconciliation(partnerId);
      setRecon(r);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setReconLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    if (tab === 'reconciliation' && config?.connected && !recon) loadRecon();
  }, [tab, config?.connected, recon, loadRecon]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const r = await rmmService.sync(partnerId);
      setSyncResult(r);
      if (!r.success) {
        setError(r.error || 'Sync failed');
        return;
      }
      await loadConfig(true);
      await loadOrgs();
      if (recon) await loadRecon();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveInterval = async (hours: number) => {
    setSavingInterval(true);
    setError(null);
    try {
      const saved = await rmmService.updateSyncInterval(partnerId, hours);
      if (!serverReportsSyncInterval(saved) || saved.syncIntervalHours !== hours) {
        setError('The automatic pull interval was left unchanged.');
        return;
      }
      setConfig(saved);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setSavingInterval(false);
    }
  };

  const handleSaveMappings = async () => {
    setSavingMaps(true);
    setError(null);
    try {
      const mappings: RmmMappingInput[] = orgs
        .map((o) => {
          const ed = edits[o.externalOrgId];
          if (!ed) return null;
          if (ed.status === 'Ignored')
            return { externalOrgId: o.externalOrgId, externalOrgName: o.externalOrgName, status: 'Ignored' };
          if (ed.customerId)
            return {
              externalOrgId: o.externalOrgId,
              externalOrgName: o.externalOrgName,
              customerId: ed.customerId,
              status: 'Confirmed',
            };
          return null;
        })
        .filter(Boolean) as RmmMappingInput[];
      await rmmService.saveMappings(partnerId, mappings);
      await loadOrgs();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setSavingMaps(false);
    }
  };

  const statusTone = useMemo(() => {
    if (!config) return { variant: 'secondary' as any, label: 'Loading…' };
    if (config.connected) return { variant: 'success' as any, label: 'Connected' };
    if (config.status === 'Error') return { variant: 'destructive' as any, label: 'Error' };
    return { variant: 'secondary' as any, label: 'Not connected' };
  }, [config]);

  if (!partnerId) {
    return <div className="p-8 text-slate-600">No partner context available.</div>;
  }

  return (
    <div className="w-full min-w-0 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-orange-100 text-orange-600 inline-flex items-center justify-center">
            <Network className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">NinjaRMM</h1>
            <p className="text-sm text-slate-500">
              Reconcile NinjaOne device counts against Aitryx installations.
            </p>
          </div>
        </div>
        <Badge variant={statusTone.variant}>{statusTone.label}</Badge>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {config?.connected && !loading && (
        <NinjaDeviceSyncStatus
          lastSyncedAt={latestApiInstant(config.lastSyncedAt, syncResult?.success ? syncResult.syncedAt : null)}
          syncIntervalHours={config.syncIntervalHours}
          scheduleSupported={serverReportsSyncInterval(config)}
          nextSyncAt={config.nextSyncAt}
          syncing={syncing}
          savingInterval={savingInterval}
          syncMessage={
            syncResult?.success
              ? devicePullSummary(syncResult.deviceCount, syncResult.organizationCount, syncResult.devicesWithSerial)
              : null
          }
          onSync={handleSync}
          onSaveInterval={handleSaveInterval}
        />
      )}

      <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
        <TabsList>
          <TabsTrigger value="connection">Connection</TabsTrigger>
          <TabsTrigger value="organizations" disabled={!config?.connected}>
            Organizations
          </TabsTrigger>
          <TabsTrigger value="reconciliation" disabled={!config?.connected}>
            Reconciliation
          </TabsTrigger>
        </TabsList>

        {/* ── Connection ─────────────────────────────────────────── */}
        <TabsContent value="connection" className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="p-6 text-slate-500">Loading…</CardContent>
            </Card>
          ) : config?.connected && !editing ? (
            <Card>
              <CardHeader>
                <CardTitle>Connected to NinjaOne</CardTitle>
                <CardDescription>{config.displayName || 'NinjaOne account'}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-slate-400">Region</div>
                    <div className="font-medium uppercase">{config.region}</div>
                  </div>
                  <div>
                    <div className="text-slate-400">Client ID</div>
                    <div className="font-mono text-xs break-all">{config.clientId}</div>
                  </div>
                  <div>
                    <div className="text-slate-400">Last tested</div>
                    <div>{fmtDate(config.lastTestedAt)}</div>
                  </div>
                  <div>
                    <div className="text-slate-400">Last synced</div>
                    <div>{fmtDate(config.lastSyncedAt)}</div>
                  </div>
                </div>
                {testResult && (
                  <div
                    className={`flex items-center gap-2 ${testResult.success ? 'text-emerald-600' : 'text-rose-600'}`}
                  >
                    {testResult.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    {testResult.text}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button variant="outline" onClick={handleTest} disabled={testing}>
                    {testing ? 'Testing…' : 'Test connection'}
                  </Button>
                  <Button variant="secondary" onClick={() => setEditing(true)}>
                    Update credentials
                  </Button>
                  <Button variant="destructive" onClick={handleDisconnect}>
                    Disconnect
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Connect NinjaOne</CardTitle>
                  <CardDescription>
                    Read-only — Aitryx only reads device inventory (Monitoring scope).
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="region">Region</Label>
                      <select
                        id="region"
                        value={form.region}
                        onChange={(e) => setForm({ ...form, region: e.target.value })}
                        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
                      >
                        {REGIONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="displayName">Display name (optional)</Label>
                      <Input
                        id="displayName"
                        value={form.displayName}
                        onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                        placeholder="Our NinjaOne account"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="clientId">Client ID</Label>
                      <Input
                        id="clientId"
                        value={form.clientId}
                        onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                        placeholder="NinjaOne API client id"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="clientSecret">Client Secret</Label>
                      <Input
                        id="clientSecret"
                        type="password"
                        value={form.clientSecret}
                        onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
                        placeholder={config?.hasSecret ? '•••••• (leave blank to keep)' : 'NinjaOne API client secret'}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {testResult && (
                    <div
                      className={`flex items-center gap-2 text-sm ${testResult.success ? 'text-emerald-600' : 'text-rose-600'}`}
                    >
                      {testResult.success ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                      {testResult.text}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={handleTest} disabled={testing || !form.clientId || !form.clientSecret}>
                      {testing ? 'Testing…' : 'Test connection'}
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </Button>
                    {config?.connected && (
                      <Button variant="ghost" onClick={() => { setEditing(false); setTestResult(null); }}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">How to get NinjaOne API credentials</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-slate-600 space-y-1">
                  <p>In NinjaOne: <strong>Administration → Apps → API → Add</strong> a client app:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Application platform: <strong>API Services (machine-to-machine)</strong></li>
                    <li>Scopes: <strong>Monitoring</strong> only</li>
                    <li>Allowed grant types: <strong>Client Credentials</strong></li>
                    <li>Redirect URI: any value (e.g. <code>https://localhost</code>) — unused</li>
                  </ul>
                  <p>Copy the Client ID and Client Secret here, pick your region, and Test.</p>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ── Organizations ──────────────────────────────────────── */}
        <TabsContent value="organizations" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Organizations</CardTitle>
                  <CardDescription>Map NinjaOne organizations to Aitryx customers. Sync now pulls the current device list.</CardDescription>
                </div>
                <Button variant="outline" onClick={handleSync} disabled={syncing}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing…' : 'Sync now'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {syncResult?.success && (
                <div className="mb-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                  {devicePullSummary(syncResult.deviceCount, syncResult.organizationCount, syncResult.devicesWithSerial)}
                </div>
              )}

              {orgsLoading ? (
                <div className="text-slate-500 py-8 text-center">Loading organizations…</div>
              ) : orgs.length === 0 ? (
                <div className="text-slate-500 py-8 text-center">
                  <Plug className="h-6 w-6 mx-auto mb-2 text-slate-400" />
                  No organizations loaded yet. Click “Sync now”.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-400 border-b border-slate-200">
                          <th className="py-2 pr-3 font-medium">NinjaOne organization</th>
                          <th className="py-2 px-3 font-medium">Devices</th>
                          <th className="py-2 px-3 font-medium">Aitryx customer</th>
                          <th className="py-2 px-3 font-medium">Match</th>
                          <th className="py-2 pl-3 font-medium">Ignore</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orgs.map((o) => {
                          const ed = edits[o.externalOrgId] || { customerId: '', status: 'Confirmed' as const };
                          const ignored = ed.status === 'Ignored';
                          return (
                            <tr key={o.externalOrgId} className={`border-b border-slate-100 ${ignored ? 'opacity-50' : ''}`}>
                              <td className="py-2 pr-3 font-medium text-slate-800">{o.externalOrgName}</td>
                              <td className="py-2 px-3 text-slate-600">{o.deviceCount}</td>
                              <td className="py-2 px-3">
                                <select
                                  value={ed.customerId}
                                  disabled={ignored}
                                  onChange={(e) =>
                                    setEdits((prev) => ({
                                      ...prev,
                                      [o.externalOrgId]: { customerId: e.target.value, status: 'Confirmed' },
                                    }))
                                  }
                                  className="w-full max-w-[16rem] rounded-md border border-slate-300 px-2 py-1.5 text-sm bg-white disabled:bg-slate-50"
                                >
                                  <option value="">— Select customer —</option>
                                  {customers.map((c) => (
                                    <option key={c.customerId} value={c.customerId}>
                                      {c.customerName}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="py-2 px-3">
                                <ConfidenceBadge confidence={o.status === 'Confirmed' ? 'manual' : o.matchConfidence} />
                              </td>
                              <td className="py-2 pl-3">
                                <input
                                  type="checkbox"
                                  checked={ignored}
                                  onChange={(e) =>
                                    setEdits((prev) => ({
                                      ...prev,
                                      [o.externalOrgId]: {
                                        customerId: e.target.checked ? '' : prev[o.externalOrgId]?.customerId || '',
                                        status: e.target.checked ? 'Ignored' : 'Confirmed',
                                      },
                                    }))
                                  }
                                  className="h-4 w-4"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex justify-end pt-4">
                    <Button onClick={handleSaveMappings} disabled={savingMaps}>
                      {savingMaps ? 'Saving…' : 'Save mappings'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Reconciliation ─────────────────────────────────────── */}
        <TabsContent value="reconciliation" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Reconciliation</CardTitle>
                  <CardDescription>
                    Each Ninja device is in one list: Ninja stale, Aitryx not installed, Aitryx not signed in,
                    Aitryx offline, or Aitryx online. This report uses the last pull.
                    Sync now, above, pulls a fresh device list first.
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={loadRecon} disabled={reconLoading}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${reconLoading ? 'animate-spin' : ''}`} />
                  {reconLoading ? 'Loading…' : 'Refresh'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {reconLoading && !recon ? (
                <div className="text-slate-500 py-8 text-center">Reconciling…</div>
              ) : !recon ? (
                <div className="text-slate-500 py-8 text-center">No reconciliation yet.</div>
              ) : recon.customers.length === 0 ? (
                <div className="text-slate-500 py-8 text-center">
                  No confirmed organization mappings yet — map organizations to customers first.
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <RmmFleetStatus recon={recon} />
                  </div>
                  {recon.unmappedOrgCount > 0 && (
                    <div className="text-xs text-slate-500 mb-3">
                      {recon.unmappedOrgCount} synced organization(s) not yet mapped — map them on the Organizations tab.
                    </div>
                  )}
                  <div className="space-y-3">
                    {recon.customers.map((c) => (
                      <CustomerRecon key={c.externalOrgId} c={c} />
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
